import cn from 'classnames'
import type { FC } from 'react'
import { useNetworkStatus } from './use-network-status.js'
import { useStorageQuota } from './use-storage-quota.js'
import styles from './offline-banner.module.css'

interface Copy {
  text: string
  tone: 'info' | 'warning'
}

const copyByStatus: Record<string, Copy> = {
  offline: { text: 'Offline — changes saved locally', tone: 'info' },
  reconnecting: { text: 'Reconnecting…', tone: 'info' },
  degraded: { text: 'Connection unstable', tone: 'warning' },
}

const STORAGE_CTA = 'Storage almost full'

export const OfflineBanner: FC = () => {
  const { status } = useNetworkStatus()
  const storage = useStorageQuota()
  const networkCopy = copyByStatus[status]
  const storageWarning = storage?.warning ?? false

  const hasContent = Boolean(networkCopy) || storageWarning

  // Storage warning is always "warning" tone; it dominates a co-existing
  // info-tone network state. When the network is fine but storage isn't,
  // we still show the strip.
  const tone: 'info' | 'warning' = storageWarning ? 'warning' : (networkCopy?.tone ?? 'info')

  // Compose the visible text. If both surface, network state first then a
  // separator and the storage CTA so the most actionable info is closest to
  // the user's attention.
  const segments: string[] = []
  if (networkCopy) {
    segments.push(networkCopy.text)
  }
  if (storageWarning) {
    segments.push(STORAGE_CTA)
  }

  // The live region is ALWAYS rendered (idle = visually hidden) so screen
  // readers reliably announce status changes — a polite region inserted only
  // when content appears is announced inconsistently because it didn't exist
  // in the DOM at the time of the change.
  return (
    <div role="status" aria-live="polite" className={hasContent ? cn(styles.banner, styles[tone]) : styles.idle}>
      {hasContent ? segments.join(' · ') : ''}
    </div>
  )
}
